import {theme} from "antd";
import type {ThemeConfig} from "antd";

const shared: ThemeConfig["token"] = {
    fontFamily: "'Geist Variable', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif",
    fontFamilyCode: "'Geist Mono Variable', ui-monospace, monospace",
    fontSize: 14,
    borderRadius: 8,
    borderRadiusLG: 12,
    borderRadiusSM: 6,
    controlHeight: 36,
    controlHeightLG: 44,
    controlHeightSM: 28,
    motionDurationMid: "0.16s",
    motionDurationSlow: "0.22s",
};

const light: ThemeConfig = {
    algorithm: theme.defaultAlgorithm,
    token: {
        ...shared,
        colorPrimary: "#171717",
        colorInfo: "#0068d6",
        colorLink: "#0068d6",
        colorSuccess: "#15803d",
        colorError: "#dc2626",
        colorText: "#171717",
        colorTextSecondary: "#525252",
        colorTextTertiary: "#6e6e6e",
        colorBorder: "#e0e0e0",
        colorBorderSecondary: "#ececec",
        colorBgLayout: "#fafafa",
        colorBgContainer: "#ffffff",
        colorBgElevated: "#ffffff",
        controlOutline: "rgba(0, 104, 214, 0.18)",
        boxShadowSecondary: "0 16px 40px -12px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.06)",
    },
};

const dark: ThemeConfig = {
    algorithm: theme.darkAlgorithm,
    token: {
        ...shared,
        colorPrimary: "#ededed",
        colorTextLightSolid: "#0a0a0a",
        colorInfo: "#3b9eff",
        colorLink: "#3b9eff",
        colorSuccess: "#3fbf6a",
        colorError: "#f26d6d",
        colorText: "#ededed",
        colorTextSecondary: "#a3a3a3",
        colorTextTertiary: "#8f8f8f",
        colorBorder: "#2e2e2e",
        colorBorderSecondary: "#242424",
        colorBgLayout: "#0a0a0a",
        colorBgContainer: "#111111",
        colorBgElevated: "#161616",
        colorBgMask: "rgba(0, 0, 0, 0.6)",
        controlOutline: "rgba(59, 158, 255, 0.25)",
        boxShadowSecondary: "0 16px 40px -12px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.06)",
    },
};

const components: ThemeConfig["components"] = {
    Button: {
        fontWeight: 500,
        primaryShadow: "none",
        defaultShadow: "none",
        dangerShadow: "none",
    },
    Modal: {titleFontSize: 16},
    Notification: {width: 360},
    Input: {activeShadow: "0 0 0 3px var(--accent-soft)", activeBorderColor: "var(--accent)", hoverBorderColor: "var(--border-strong)"},
};

const darkComponents: ThemeConfig["components"] = {
    ...components,
    Button: {
        ...components?.Button,
        fontWeight: 450,
        colorPrimary: "#ededed",
        colorPrimaryHover: "#ffffff",
        colorPrimaryActive: "#d4d4d4",
    },
    Checkbox: {colorPrimary: "#3b9eff", colorPrimaryHover: "#5aaeff"},
    Tooltip: {colorTextLightSolid: "#ededed", colorBgSpotlight: "#262626"},
};

export function antdTheme(isDark: boolean, reduceMotion: boolean): ThemeConfig {
    const base = isDark ? dark : light;
    return {...base, token: {...base.token, motion: !reduceMotion}, components: isDark ? darkComponents : components};
}
